import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Plus, Save, X, ExternalLink, RefreshCw, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { ROLE_LABEL } from "@/lib/statusMap";

const ROLES = ["applicant", "hod", "admin1", "admin1_alt", "admin2", "process_owner", "super_admin"];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/users")
      .then((r) => setUsers(r.data))
      .catch(() => toast.error("Could not load users"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async () => {
    try {
      await api.patch(`/users/${editing.id}`, {
        role: editing.role,
        departments: editing.departments,
        active: editing.active,
        name: editing.name,
        email: editing.email,
        phone: editing.phone,
        is_super_admin: !!editing.is_super_admin,
      });
      toast.success("Updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Layout>
      <Toaster position="bottom-right" richColors />
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">Super Admin</div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1f2320]">User management</h1>
          <p className="text-sm text-[#4a524c] mt-1">Assign roles, edit departments, activate or deactivate accounts.</p>
        </div>
        <button className="btn-vgws" onClick={() => setCreating(true)} data-testid="create-user-button">
          <Plus size={16} /> New user
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2 text-sm">
        <a href="/admin/email-log" className="text-[#7a1f2b] hover:underline inline-flex items-center gap-1"
           data-testid="link-dev-email-log">
          <ExternalLink size={12} /> Dev Email Log
        </a>
      </div>

      <IntegrationsWidget />

      <div className="sheet-plain overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#6e7770]">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="admin-users-table">
              <thead>
                <tr className="bg-[#faf5eb] text-left">
                  <th className="px-4 py-3 font-serif text-[#7a1f2b]">Name</th>
                  <th className="px-4 py-3 font-serif text-[#7a1f2b]">Email</th>
                  <th className="px-4 py-3 font-serif text-[#7a1f2b]">Role</th>
                  <th className="px-4 py-3 font-serif text-[#7a1f2b]">Departments</th>
                  <th className="px-4 py-3 font-serif text-[#7a1f2b]">Active</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-[#f2ecdf]" data-testid={`user-row-${u.email}`}>
                    <td className="px-4 py-3 font-medium text-[#1f2320]">{u.name}</td>
                    <td className="px-4 py-3 text-[#4a524c]">{u.email}</td>
                    <td className="px-4 py-3 text-[#4a524c]">
                      {ROLE_LABEL[u.role] || u.role}
                      {u.is_super_admin && u.role !== "super_admin" && (
                        <span className="ml-2 badge bg-[#f9e8ec] text-[#7a1f2b] border-[#7a1f2b]/40"
                              data-testid={`super-flag-${u.email}`}>
                          + Super Admin
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6e7770]">{(u.departments || []).join(", ") || "—"}</td>
                    <td className="px-4 py-3">
                      {u.active ? (
                        <span className="badge bg-emerald-100 text-emerald-800 border-emerald-300">Active</span>
                      ) : (
                        <span className="badge bg-slate-100 text-slate-700 border-slate-300">Inactive</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button className="link-vgws text-sm" onClick={() => setEditing({ ...u })} data-testid={`edit-user-${u.email}`}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <UserModal
          user={editing}
          onClose={() => setEditing(null)}
          onChange={setEditing}
          onSave={save}
          title="Edit user"
        />
      )}
      {creating && (
        <CreateUserModal onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />
      )}
    </Layout>
  );
}

function UserModal({ user, onClose, onChange, onSave, title }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="sheet w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="user-edit-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl font-bold text-[#7a1f2b]">{title}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <label className="block">
            <span className="text-sm font-medium">Name</span>
            <input className="input-vgws mt-1" value={user.name}
                   onChange={(e) => onChange({ ...user, name: e.target.value })}
                   data-testid="edit-user-name" />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Email</span>
            <input className="input-vgws mt-1" type="email" value={user.email || ""}
                   onChange={(e) => onChange({ ...user, email: e.target.value.toLowerCase().trim() })}
                   data-testid="edit-user-email" />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Phone</span>
            <input className="input-vgws mt-1" value={user.phone || ""}
                   onChange={(e) => onChange({ ...user, phone: e.target.value })}
                   data-testid="edit-user-phone" />
          </label>
          <label className="block">
            <span className="text-sm font-medium">Role</span>
            <select className="input-vgws mt-1" value={user.role}
                    onChange={(e) => onChange({ ...user, role: e.target.value })}
                    data-testid="edit-user-role">
              {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium">Departments (comma-separated)</span>
            <input className="input-vgws mt-1"
                   value={(user.departments || []).join(", ")}
                   onChange={(e) => onChange({ ...user, departments: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                   data-testid="edit-user-departments" />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!user.active}
                   onChange={(e) => onChange({ ...user, active: e.target.checked })}
                   data-testid="edit-user-active" />
            Active
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!user.is_super_admin}
                   onChange={(e) => onChange({ ...user, is_super_admin: e.target.checked })}
                   data-testid="edit-user-is-super-admin" />
            Grant Super Admin bypass (keeps primary role intact)
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-vgws" onClick={onSave} data-testid="save-user-button"><Save size={16} /> Save</button>
        </div>
      </div>
    </div>
  );
}

function CreateUserModal({ onClose, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", role: "applicant", departments: [] });
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="sheet w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="user-create-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl font-bold text-[#7a1f2b]">Create user</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <input className="input-vgws" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="create-user-name" />
          <input className="input-vgws" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="create-user-email" />
          <select className="input-vgws" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="create-user-role">
            {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
          </select>
          <input className="input-vgws" placeholder="Departments (comma-separated)" onChange={(e) => setForm({ ...form, departments: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} />
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-vgws" disabled={busy} onClick={create} data-testid="submit-create-user">{busy ? "Creating…" : "Create user"}</button>
        </div>
      </div>
    </div>
  );
}


function StatusPill({ ok, mocked }) {
  if (mocked) {
    return (
      <span className="badge bg-amber-100 text-amber-800 border-amber-300 inline-flex items-center gap-1">
        <AlertTriangle size={12} /> MOCKED
      </span>
    );
  }
  if (ok) {
    return (
      <span className="badge bg-emerald-100 text-emerald-800 border-emerald-300 inline-flex items-center gap-1">
        <CheckCircle2 size={12} /> Live
      </span>
    );
  }
  return (
    <span className="badge bg-rose-100 text-rose-800 border-rose-300 inline-flex items-center gap-1">
      <XCircle size={12} /> Down
    </span>
  );
}

function IntegrationsWidget() {
  const [state, setState] = useState({ loading: true, drive: null, smtp: null });

  const run = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await api.post("/admin/integrations/healthcheck");
      setState({ loading: false, drive: r.data.drive, smtp: r.data.smtp });
    } catch (e) {
      toast.error(formatApiError(e));
      setState({ loading: false, drive: null, smtp: null });
    }
  };
  useEffect(() => { run(); }, []);

  return (
    <section className="sheet p-5 mb-6" data-testid="integrations-widget">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">
            Integrations
          </div>
          <h2 className="font-serif text-xl font-bold text-[#1f2320]">External services</h2>
        </div>
        <button className="btn-ghost" onClick={run} disabled={state.loading} data-testid="integrations-refresh">
          <RefreshCw size={14} className={state.loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {/* Drive card */}
        <div className="rounded-lg border border-[#e2d7c3] bg-[#faf5eb] p-4" data-testid="integration-drive">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="font-serif font-semibold text-[#7a1f2b]">Google Drive</div>
            <StatusPill ok={state.drive?.ok} mocked={false} />
          </div>
          <p className="text-xs text-[#4a524c] mb-2">
            Auto-sync of form PDFs, attachments and audit trail to a shared Drive folder.
          </p>
          <div className="text-xs text-[#6e7770] break-words">
            <div><span className="font-medium">Status:</span> {state.drive?.detail || "—"}</div>
            {state.drive?.sa_email && (
              <div className="mt-1"><span className="font-medium">Service account:</span> {state.drive.sa_email}</div>
            )}
          </div>
          {state.drive?.folder_link && (
            <a
              href={state.drive.folder_link}
              target="_blank" rel="noopener noreferrer"
              className="btn-vgws btn-teal mt-3 w-full justify-center"
              data-testid="drive-open-root"
            >
              <ExternalLink size={14} /> Open root folder
            </a>
          )}
        </div>

        {/* SMTP card */}
        <div className="rounded-lg border border-[#e2d7c3] bg-[#faf5eb] p-4" data-testid="integration-smtp">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="font-serif font-semibold text-[#7a1f2b]">Gmail SMTP</div>
            <StatusPill ok={state.smtp?.ok} mocked={!!state.smtp?.mocked} />
          </div>
          <p className="text-xs text-[#4a524c] mb-2">
            Email notifications for approvers, applicants and TAT escalations.
          </p>
          <div className="text-xs text-[#6e7770] break-words">
            <span className="font-medium">Status:</span> {state.smtp?.detail || "—"}
          </div>
        </div>
      </div>
    </section>
  );
}
